import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Input, InputNumber, Space, Tooltip, Tabs, Switch, Statistic, Descriptions, Popconfirm, Modal, Select, Form, Dropdown, Checkbox } from "antd";
import {
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  EyeOutlined,
  DownloadOutlined,
  CreditCardOutlined,
  StopOutlined,
  UserAddOutlined,
  LockOutlined,
  MoreOutlined,
  CopyOutlined,
  DollarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import { useDebounce } from "../../../../core/hooks/useDebounce";
import { updateCompanyInCache } from "../../../../core/data/redux/slices/adminCompanySlice";
import { cancelUserSubscription, fetchAdminPlans } from "../../../../core/data/redux/slices/PlanSlice";
import EditCompanyOffcanvas from "./EditCompanyOffcanvas";
import EditAgentOffcanvas from "./EditAgentOffcanvas";

const CompanyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initialCompany = location.state?.company || null;
  const [companyDetails, setCompanyDetails] = useState(initialCompany);


  
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsPagination, setAgentsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);
  const [editCompanyVisible, setEditCompanyVisible] = useState(false);
  const [editAgentVisible, setEditAgentVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [accountToggleLoading, setAccountToggleLoading] = useState(false);
  const [agentLoadingToggles, setAgentLoadingToggles] = useState({});
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingPage, setBillingPage] = useState(1);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [activeTab, setActiveTab] = useState("agents");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [assignPlanId, setAssignPlanId] = useState(null);
  const [assignPeriod, setAssignPeriod] = useState("monthly");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignPaymentStatus, setAssignPaymentStatus] = useState("full"); // "full"|"partial"|"pending"
  const [assignAmountPaid, setAssignAmountPaid] = useState(0);
  const [markPaidModal, setMarkPaidModal] = useState(false);
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [markPaidAmount, setMarkPaidAmount] = useState(0);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const { plans: allPlans } = useSelector((state) => state.plans);

  // ── Agent management modals ───────────────────────────────────────────────
  const [resetPwdModal, setResetPwdModal] = useState(false);
  const [resetPwdTarget, setResetPwdTarget] = useState(null);
  const [resetPwdForm] = Form.useForm();
  const [resetPwdLoading, setResetPwdLoading] = useState(false);

  const [contactModal, setContactModal] = useState(false);
  const [contactTarget, setContactTarget] = useState(null);
  const [contactForm] = Form.useForm();
  const [contactLoading, setContactLoading] = useState(false);

  // ── Create Agent modal ────────────────────────────────────────────────────
  const [createAgentModal, setCreateAgentModal] = useState(false);
  const [createAgentForm] = Form.useForm();
  const [createAgentLoading, setCreateAgentLoading] = useState(false);

  // ── Adjust Balance modal ──────────────────────────────────────────────────
  const [balanceModal, setBalanceModal] = useState(false);
  const [balanceForm] = Form.useForm();
  const [balanceSaving, setBalanceSaving] = useState(false);
  const [liveBalance, setLiveBalance] = useState(companyDetails?.creditBalance ?? null);

  const openBalanceModal = () => {
    balanceForm.setFieldsValue({
      mode: "set",
      amount: Number(liveBalance ?? companyDetails?.creditBalance ?? 0).toFixed(2),
      note: "",
    });
    setBalanceModal(true);
  };

  const handleSaveBalance = async () => {
    const values = await balanceForm.validateFields();
    setBalanceSaving(true);
    try {
      const res = await api.put("/superAdmin/adjustBalance", {
        userId: companyDetails._id,
        amount: Number(values.amount),
        mode:   values.mode,
        note:   values.note || "",
      });
      const nb = Number(res.data.newBalance);
      setLiveBalance(nb);
      setCompanyDetails((prev) => ({ ...prev, creditBalance: nb }));
      setBalanceModal(false);
      dispatch(showToast({ message: `Balance updated — $${nb.toFixed(2)}`, variant: "success" }));
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to update balance", variant: "danger" }));
    } finally {
      setBalanceSaving(false);
    }
  };

  const [localExtensionStatus, setLocalExtensionStatus] = useState(
    initialCompany?.extensionStatus || false
  );
  const [localAccountStatus, setLocalAccountStatus] = useState(
    initialCompany?.accountStatus || "active"
  );
  const [autoTranscribeEnabled, setAutoTranscribeEnabled] = useState(
    initialCompany?.autoTranscribeEnabled || false
  );
  const [savingTranscribe, setSavingTranscribe] = useState(false);

  // Fetch agents when component mounts, pagination changes or debounced search changes
  useEffect(() => {
    if (companyDetails?._id) {
      fetchAgents(
        companyDetails._id,
        agentsPagination.current,
        agentsPagination.pageSize,
        debouncedSearch
      );
    }
    // eslint-disable-next-line
  }, [
    companyDetails?._id,
    agentsPagination.current,
    agentsPagination.pageSize,
    // Note: we do not include debouncedSearch here intentionally because we handle it separately
  ]);

  // When debounced search term changes, reset to first page (or fetch directly if already on page 1)
  useEffect(() => {
    if (!companyDetails?._id) return;

    // If page is not 1, set it to 1 which will trigger the main effect to fetch using debouncedSearch
    if (agentsPagination.current !== 1) {
      setAgentsPagination((prev) => ({ ...prev, current: 1 }));
    } else {
      // If already on page 1, fetch directly with the debounced search term
      fetchAgents(
        companyDetails._id,
        1,
        agentsPagination.pageSize,
        debouncedSearch
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // keep local switch state in sync when company details change
  useEffect(() => {
    setLocalExtensionStatus(Boolean(companyDetails?.extensionStatus));
    setLocalAccountStatus(companyDetails?.accountStatus || "active");
    setAutoTranscribeEnabled(Boolean(companyDetails?.autoTranscribeEnabled));
  }, [companyDetails?.extensionStatus, companyDetails?.accountStatus, companyDetails?.autoTranscribeEnabled]);

  const handleTranscribeToggle = async (value) => {
    setAutoTranscribeEnabled(value);
    setSavingTranscribe(true);
    try {
      await api.patch("/editProfile/ai-settings", {
        userId: companyDetails._id,
        autoTranscribeEnabled: value,
      });
    } catch (err) {
      setAutoTranscribeEnabled(!value);
    } finally {
      setSavingTranscribe(false);
    }
  };
  const fetchAgents = async (adminId, page = 1, limit = 10, search = "") => {
    setAgentsLoading(true);
    try {
      const response = await api.post("/superAdmin/allAgentsOfCompanyAdmin", {
        adminId,
        page,
        limit,
        search,
      });
      if (response.data.status === "success") {
        setAgents(response.data.data);
        setAgentsPagination({
          current: response.data.page,
          pageSize: response.data.limit,
          total: response.data.totalAgents,
        });
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message: "Failed to fetch agents",
          variant: "danger",
        })
      );
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleAgentsTableChange = (pag) => {
    setAgentsPagination({ ...pag, total: pag.total });
  };

  const handleSearch = () => {
    setAgentsPagination((prev) => ({ ...prev, current: 1 }));
    if (companyDetails?._id) {
      fetchAgents(companyDetails._id, 1, agentsPagination.pageSize, searchText);
    }
  };

  const handleEditCompany = () => {
    setEditCompanyVisible(true);
  };

  const handleToggleExtensionStatus = async (checked) => {
    if (!companyDetails?._id) return;
    setToggleLoading(true);
    try {
      // Call backend to update only the status. Backend will validate required fields when activating.
      // Include current extension fields so backend can validate activation
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId: companyDetails._id,
        extensionNumber: companyDetails.extensionNumber,
        telephone: companyDetails.telephone,
        sipSecret: companyDetails.sipSecret,
        status: checked,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Refetch company details from server to reflect any DB-side changes
        const detailsRes = await api.post(
          "/superAdmin/companyAdminDetailsById",
          {
            adminId: companyDetails._id,
          }
        );

        console.log(detailsRes,"bhjgvcfxcgvbnm");
        
        if (detailsRes.data.status === "success") {
          const updatedCompany = detailsRes.data.data;
          setCompanyDetails(updatedCompany);
          dispatch(
            updateCompanyInCache({
              companyId: updatedCompany._id,
              updates: updatedCompany,
            })
          );
          // Update local switch state and show success toast
          setLocalExtensionStatus(Boolean(updatedCompany.extensionStatus));
          navigate(location.pathname, {
            replace: true,
            state: {
              company: updatedCompany,
            },
          });
          dispatch(
            showToast({
              heading: "Success",
              message: updatedCompany.extensionStatus
                ? "Extension activated successfully"
                : "Extension deactivated successfully",
              variant: "success",
            })
          );
        }
      }
    } catch (error) {
      const msg = error?.response?.data?.error || "Failed to update status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
      // revert switch state
      setLocalExtensionStatus(Boolean(companyDetails?.extensionStatus));
    } finally {
      setToggleLoading(false);
    }
  };

  const handleToggleAccountStatus = async (checked) => {
    if (!companyDetails?._id) return;
    setAccountToggleLoading(true);
    try {
      const newStatus = checked ? "active" : "suspended";
      const endpoint = checked ? "/deleteUser/activate" : "/deleteUser/suspend";

      const response = await api.post(endpoint, {
        user_id: companyDetails._id,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        // await new Promise((resolve) => setTimeout(resolve, 800));

        // Refetch company details from server
        const detailsRes = await api.post(
          "/superAdmin/companyAdminDetailsById",
          {
            adminId: companyDetails._id,
          }
        );

        console.log(detailsRes,"bhjgvcfxcgvbnm");
        
        if (detailsRes.data.status === "success") {
          const updatedCompany = detailsRes.data.data;
          setCompanyDetails(updatedCompany);
          navigate(location.pathname, {
            replace: true,
            state: {
              company: updatedCompany,
            },
          });
          fetchAgents(
            updatedCompany._id,
            1,
            agentsPagination.pageSize,
            searchText
          );
          dispatch(
            showToast({
              heading: "Success",
              message: response.data.message,
              variant: "success",
            })
          );
        }
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update account status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
      // revert switch state
      setLocalAccountStatus(companyDetails?.accountStatus || "active");
    } finally {
      setAccountToggleLoading(false);
    }
  };

  const handleCopyLoginLink = async (userId) => {
    try {
      const res = await api.post("/superAdmin/generateLoginLink", { userId });
      await navigator.clipboard.writeText(res.data.link);
      dispatch(showToast({ message: "Login link copied to clipboard", variant: "success" }));
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to generate login link", variant: "danger" }));
    }
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setEditAgentVisible(true);
  };

  const openAgentResetPwd = (agent) => {
    setResetPwdTarget(agent);
    resetPwdForm.resetFields();
    setResetPwdModal(true);
  };

  const handleAgentResetPassword = async () => {
    try {
      const values = await resetPwdForm.validateFields();
      setResetPwdLoading(true);
      await api.post("/superAdmin/resetUserPassword", { userId: resetPwdTarget._id, newPassword: values.newPassword });
      dispatch(showToast({ message: "Password reset successfully", variant: "success" }));
      setResetPwdModal(false);
    } catch (err) {
      if (err?.errorFields) return;
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to reset password", variant: "error" }));
    } finally {
      setResetPwdLoading(false);
    }
  };

  const openAgentContactModal = (agent) => {
    setContactTarget(agent);
    contactForm.setFieldsValue({
      email: agent.email || "",
      mobile: agent.phonenumbers?.[0]?.number || "",
      countryCode: agent.phonenumbers?.[0]?.countryCode || "",
    });
    setContactModal(true);
  };

  const handleAgentUpdateContact = async () => {
    try {
      const values = await contactForm.validateFields();
      setContactLoading(true);
      await api.put("/superAdmin/updateUserContact", {
        userId: contactTarget._id,
        email: values.email,
        mobile: values.mobile ? { countryCode: values.countryCode || "", number: values.mobile } : undefined,
      });
      dispatch(showToast({ message: "Contact updated successfully", variant: "success" }));
      setContactModal(false);
      fetchAgents(companyDetails._id, agentsPagination.current, agentsPagination.pageSize, searchText);
    } catch (err) {
      if (err?.errorFields) return;
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to update contact", variant: "error" }));
    } finally {
      setContactLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    try {
      const values = await createAgentForm.validateFields();
      setCreateAgentLoading(true);
      const { emailVerified, mobileVerified, countryCode, mobile, ...rest } = values;
      await api.post("/superAdmin/createAgent", {
        ...rest,
        mobile: mobile ? { countryCode: countryCode || "", number: mobile } : undefined,
        companyAdminId: companyDetails._id,
        isVerified: !!emailVerified,
        emailVerified: !!emailVerified,
        mobileVerified: !!mobileVerified,
      });
      dispatch(showToast({ message: "Agent created successfully", variant: "success" }));
      setCreateAgentModal(false);
      createAgentForm.resetFields();
      fetchAgents(companyDetails._id, 1, agentsPagination.pageSize, searchText);
    } catch (err) {
      if (err?.errorFields) return;
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to create agent", variant: "danger" }));
    } finally {
      setCreateAgentLoading(false);
    }
  };

  const handleDeleteAgent = (record) => {
    Modal.confirm({
      title: "Delete Agent",
      content: `Permanently delete "${[record.firstname, record.lastname].filter(Boolean).join(" ")}" (${record.email})? This cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await api.delete(`/superAdmin/deleteAgent/${record._id}`);
          dispatch(showToast({ message: "Agent deleted successfully", variant: "success" }));
          fetchAgents(companyDetails._id, agentsPagination.current, agentsPagination.pageSize, searchText);
        } catch (err) {
          dispatch(showToast({ message: err?.response?.data?.error || "Failed to delete agent", variant: "danger" }));
        }
      },
    });
  };

  const handleCompanyOffcanvasClose = async (updated = false) => {
    setEditCompanyVisible(false);

    // Refetch company details if updated
    if (updated && companyDetails?._id) {
      try {
        const response = await api.post("/superAdmin/companyAdminDetailsById", {
          adminId: companyDetails._id,
        });


        console.log(response.data,"bhjgvcfxcgvbnm");
        
        if (response.data.status === "success") {
          const updatedCompany = response.data.data;
          setCompanyDetails(updatedCompany);
          navigate(location.pathname, {
            replace: true,
            state: {
              company: updatedCompany,
            },
          });

          // Update in Redux cache
          dispatch(
            updateCompanyInCache({
              companyId: updatedCompany._id,
              updates: updatedCompany,
            })
          );
          await fetchAgents(
            updatedCompany._id,
            1,
            agentsPagination.pageSize,
            searchText
          );
        }
      } catch (error) {
        console.error("Error refetching company details:", error);
      }
    }
  };

  const handleAgentOffcanvasClose = async (updated = false) => {
    setEditAgentVisible(false);
    setSelectedAgent(null);

    // Refetch agents list if updated
    if (updated && companyDetails?._id) {
      fetchAgents(
        companyDetails._id,
        agentsPagination.current,
        agentsPagination.pageSize,
        searchText
      );
    }
  };

  const handleToggleAgentAccountStatus = async (agentId, currentStatus) => {
    const toggleKey = `agent-account-${agentId}`;
    setAgentLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const newStatus = currentStatus === "active" ? "suspended" : "active";
      const endpoint =
        newStatus === "active" ? "/deleteUser/activate" : "/deleteUser/suspend";

      const response = await api.post(endpoint, {
        user_id: agentId,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: response.data.message,
            variant: "success",
          })
        );
        // Refresh agents list
        await fetchAgents(
          companyDetails._id,
          agentsPagination.current,
          agentsPagination.pageSize,
          searchText
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to update agent account status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setAgentLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const handleToggleAgentExtensionStatus = async (agentId, agent, checked) => {
    const toggleKey = `agent-extension-${agentId}`;
    setAgentLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId: agentId,
        extensionNumber: agent.extensionNumber,
        telephone: agent.telephone,
        sipSecret: agent.sipSecret,
        status: checked,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: checked
              ? "Agent extension activated successfully"
              : "Agent extension deactivated successfully",
            variant: "success",
          })
        );
        // Refresh agents list
        await fetchAgents(
          companyDetails._id,
          agentsPagination.current,
          agentsPagination.pageSize,
          searchText
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        "Failed to update agent extension status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setAgentLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const handleCancelSubscription = async () => {
    if (!companyDetails?._id) return;
    setCancelLoading(true);
    try {
      await dispatch(cancelUserSubscription(companyDetails._id)).unwrap();
      await fetchBillingDetails(billingPage);
    } catch (_) {}
    finally { setCancelLoading(false); }
  };

  const handleAssignPlan = async () => {
    if (!assignPlanId || !companyDetails?._id) return;
    setAssignLoading(true);
    try {
      // Compute plan price in cents for the selected period (apply discount + period multiplier)
      const selectedPlan = (allPlans || []).find((p) => p._id === assignPlanId);
      const periodMultiplier = { monthly: 1, quarterly: 3, semiannual: 6, yearly: 12 }[assignPeriod] || 1;
      const rawPrice = selectedPlan?.pricing?.[assignPeriod]?.price || 0;
      const discountPct = selectedPlan?.pricing?.[assignPeriod]?.discountPercent || 0;
      const planPrice = rawPrice * periodMultiplier * (1 - discountPct / 100);
      const totalAmountCents = Math.round(planPrice * 100);
      const userCreditBalance = companyDetails?.creditBalance || 0; // in dollars
      const userCreditCents = Math.round(userCreditBalance * 100);

      let amountPaidCents = assignPaymentStatus === "full"
        ? totalAmountCents
        : assignPaymentStatus === "partial"
          ? Math.round(assignAmountPaid * 100)
          : 0;

      // Auto-apply credits to cover pending amount (no checkbox needed)
      const pendingCents = totalAmountCents - amountPaidCents;
      let creditDeductedCents = 0;
      if (pendingCents > 0 && userCreditCents > 0) {
        creditDeductedCents = Math.min(userCreditCents, pendingCents);
        amountPaidCents += creditDeductedCents;
      }

      await api.post("/superAdmin/plans/assign-user", {
        userId: companyDetails._id,
        planId: assignPlanId,
        billingPeriod: assignPeriod,
        paymentStatus: amountPaidCents >= totalAmountCents ? "full" : assignPaymentStatus,
        totalAmountCents,
        amountPaidCents,
        creditDeductedCents,
      });
      dispatch(showToast({ heading: "Success", message: "Plan assigned successfully", variant: "success" }));
      setAssignModal(false);
      setAssignPlanId(null);
      setAssignPaymentStatus("full");
      setAssignAmountPaid(0);
      await fetchBillingDetails(billingPage);
    } catch (err) {
      dispatch(showToast({ heading: "Error", message: err?.response?.data?.message || "Failed to assign plan", variant: "danger" }));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidInvoice) return;
    setMarkPaidLoading(true);
    try {
      await api.post("/superAdmin/invoices/mark-paid", {
        invoiceId: markPaidInvoice._id,
        amountPaid: markPaidAmount,
      });
      dispatch(showToast({ heading: "Success", message: "Invoice marked as paid", variant: "success" }));
      setMarkPaidModal(false);
      setMarkPaidInvoice(null);
      setMarkPaidAmount(0);
      await fetchBillingDetails(billingPage);
    } catch (err) {
      dispatch(showToast({ heading: "Error", message: err?.response?.data?.message || "Failed to update invoice", variant: "danger" }));
    } finally {
      setMarkPaidLoading(false);
    }
  };


  const fetchBillingDetails = async (page = 1) => {
    if (!companyDetails?._id) return;
    setBillingLoading(true);
    try {
      const res = await api.get(`/superAdmin/companies/${companyDetails._id}/billing?page=${page}&limit=10`);
      setBillingData(res.data);
    } catch (_) {}
    finally { setBillingLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "billing") {
      fetchBillingDetails(billingPage);
      if (!allPlans?.length) dispatch(fetchAdminPlans());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, billingPage]);

  const agentColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          {record.firstname ? `${record.firstname} ${record.lastname}` : "N/A"}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          {email}
        </span>
      ),
    },
    {
      title: "Extension Status",
      dataIndex: "extensionStatus",
      key: "extensionStatus",
      render: (extensionStatus, record) => {
        const toggleKey = `agent-extension-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={extensionStatus}
              loading={agentLoadingToggles[toggleKey]}
              onChange={(checked) =>
                handleToggleAgentExtensionStatus(record._id, record, checked)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Inactive"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Account Status",
      dataIndex: "accountStatus",
      key: "accountStatus",
      render: (accountStatus, record) => {
        const toggleKey = `agent-account-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={accountStatus === "active"}
              loading={agentLoadingToggles[toggleKey]}
              onChange={() =>
                handleToggleAgentAccountStatus(record._id, accountStatus)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Suspended"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Extension",
      dataIndex: "extensionNumber",
      key: "extensionNumber",
      // render: (ext) => <Tag color="green">{ext || "N/A"}</Tag>,
      render: (text, record) => {
        
        return (
         
         <Tag color="green">{record?.PBXDetails?.PBX_EXTENSION_NUMBER || "N/A"}</Tag>
        
        );
      },
    },
    {
      title: "Telephone",
      dataIndex: "telephone",
      key: "telephone",
      render: (phone, record) => (
        // <span>
        //   <PhoneOutlined style={{ marginRight: 8 }} />
        //   {phone || "N/A"}
        // </span>
        <div>{record?.PBXDetails?.PBX_TELEPHONE}</div>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        date ? (
          <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            <div>{dayjs(date).format("DD MMM YYYY")}</div>
            <div style={{ color: "#9ca3af", fontSize: 11 }}>{dayjs(date).format("hh:mm A")}</div>
          </span>
        ) : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menuItems = [
          {
            key: "edit",
            label: "Edit Agent",
            icon: <EditOutlined />,
            onClick: () => handleEditAgent(record),
          },
          {
            key: "reset-pwd",
            label: "Reset Password",
            icon: <LockOutlined />,
            onClick: () => openAgentResetPwd(record),
          },
          {
            key: "copy-login",
            label: "Copy Login Link",
            icon: <CopyOutlined />,
            onClick: () => handleCopyLoginLink(record._id),
          },
          {
            key: "change-contact",
            label: "Change Email / Mobile",
            icon: <MailOutlined />,
            onClick: () => openAgentContactModal(record),
          },
          {
            key: "delete",
            label: "Delete Agent",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDeleteAgent(record),
          },
        ];
        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 16 }} />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  const statusColor = { trial: "orange", active: "green", paused: "gold", expired: "red", cancelled: "red", none: "default" };
  const statusLabel = { trial: "Free Trial", active: "Active", paused: "Paused", expired: "Expired", cancelled: "Cancelled", none: "No Plan" };
  const periodColor = { monthly: "blue", quarterly: "purple", yearly: "cyan" };

  const invoiceColumns = [
    { title: "Invoice #", dataIndex: "invoiceNumber", key: "invoiceNumber", render: (v) => v || "—" },
    { title: "Date", dataIndex: "stripeCreatedAt", key: "date", render: (d) => d ? dayjs(d).format("MMM D, YYYY") : "—" },
    { title: "Plan", dataIndex: ["planId", "name"], key: "plan", render: (v) => v || "—" },
    {
      title: "Period",
      key: "period",
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.billingPeriodStart ? dayjs(r.billingPeriodStart).format("MMM D, YYYY") : "—"}
          {" → "}
          {r.billingPeriodEnd ? dayjs(r.billingPeriodEnd).format("MMM D, YYYY") : "—"}
        </span>
      ),
    },
    {
      title: "Amount", key: "amount",
      render: (_, r) => {
        const totalCents = r.planAmount ?? r.amount ?? 0;
        const paidCents = r.amountPaid ?? 0;
        if (r.status === "open") {
          const remaining = Math.max(0, totalCents - paidCents);
          return (
            <span>
              <span style={{ fontWeight: 600 }}>${(remaining / 100).toFixed(2)}</span>
              {paidCents > 0 && (
                <span style={{ fontSize: 11, color: "#10b981", display: "block" }}>
                  ${(paidCents / 100).toFixed(2)} credits applied
                </span>
              )}
            </span>
          );
        }
        const displayCents = paidCents > 0 ? paidCents : totalCents;
        return `$${(displayCents / 100).toFixed(2)}`;
      },
    },
    {
      title: "Status", dataIndex: "status", key: "status",
      render: (s) => <Tag color={s === "paid" ? "green" : s === "open" ? "orange" : "red"}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          {r.hostedInvoiceUrl && (
            <Tooltip title="View Invoice">
              <Button type="text" size="small" icon={<EyeOutlined />} href={r.hostedInvoiceUrl} target="_blank" />
            </Tooltip>
          )}
          {r.invoicePdf && (
            <Tooltip title="Download PDF">
              <Button type="text" size="small" icon={<DownloadOutlined />} href={r.invoicePdf} target="_blank" />
            </Tooltip>
          )}
          {r.status === "open" && (
            <Tooltip title="Mark as Paid">
              <Button
                type="text" size="small" icon={<DollarOutlined />}
                style={{ color: "#059669" }}
                onClick={() => {
                  const pending = ((r.amount || 0) - (r.amountPaid || 0)) / 100;
                  setMarkPaidInvoice(r);
                  setMarkPaidAmount(pending > 0 ? pending : (r.amount || 0) / 100);
                  setMarkPaidModal(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "agents",
      label: "Agents",
      children: (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-semibold mb-0">
              Agents List ({agentsPagination.total})
            </h5>
            <Space>
              <Input.Search
                placeholder="Search agents..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                style={{ width: 300 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => { createAgentForm.resetFields(); setCreateAgentModal(true); }}
              >
                Create Agent
              </Button>
            </Space>
          </div>
          <div className="table-responsive custom-table">
            <Table
              columns={agentColumns}
              dataSource={agents}
              rowKey="_id"
              loading={agentsLoading}
              pagination={{
                current: agentsPagination.current,
                pageSize: agentsPagination.pageSize,
                total: agentsPagination.total,
                showSizeChanger: true,
              }}
              onChange={handleAgentsTableChange}
              bordered
            />
          </div>
        </div>
      ),
    },
    {
      key: "billing",
      label: <span><CreditCardOutlined style={{ marginRight: 6 }} />Billing & Subscription</span>,
      children: (
        <div>
          {/* Summary stats — 3 cards only, details are in sections below */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
            <div style={{ background: "#f9fafb", borderRadius: 10, padding: 20, border: "1px solid #e5e7eb" }}>
              <Statistic title="Plan Status" value={statusLabel[billingData?.user?.planStatus] || "—"} />
              {billingData?.user?.planStatus && (
                <Tag color={statusColor[billingData.user.planStatus]} style={{ marginTop: 6 }}>
                  {statusLabel[billingData.user.planStatus]}
                </Tag>
              )}
            </div>

            {billingData?.subscription && (
              <div style={{ background: "#f9fafb", borderRadius: 10, padding: 20, border: "1px solid #e5e7eb" }}>
                <Statistic title="Agent Seats" value={billingData.subscription.agentCount || 1} suffix="agents" />
                {billingData.subscription.planId?.agentPrice > 0 && (
                  <span style={{ color: "#6b7280", fontSize: 12 }}>
                    ${billingData.subscription.planId.agentPrice}/agent/mo extra
                  </span>
                )}
              </div>
            )}

            <div style={{ background: "#f9fafb", borderRadius: 10, padding: 20, border: "1px solid #e5e7eb" }}>
              <Statistic title="Total Paid" value={`$${((billingData?.totalPaid || 0) / 100).toFixed(2)}`} />
              {(billingData?.totalDue || 0) > (billingData?.totalPaid || 0) && (
                <div style={{ color: "#f59e0b", fontSize: 12, marginTop: 2 }}>
                  ${((billingData.totalDue - billingData.totalPaid) / 100).toFixed(2)} pending
                </div>
              )}
              <span style={{ color: "#6b7280", fontSize: 12 }}>{billingData?.totalInvoices || 0} invoices</span>
            </div>
          </div>

          {/* Merged Plan & Subscription Details */}
          <div style={{ background: "#f9fafb", borderRadius: 10, padding: 20, marginBottom: 24, border: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h6 style={{ fontWeight: 600, margin: 0 }}>Plan & Subscription Details</h6>
              <Space>
                <Button
                  icon={<UserAddOutlined />}
                  size="small"
                  style={{ background: "#6366f1", color: "#fff", borderColor: "#6366f1" }}
                  onClick={() => {
                    setAssignModal(true);
                    // Pre-select current plan & period
                    const currentPlanId = billingData?.subscription?.planId?._id || billingData?.subscription?.planId;
                    const currentPeriod = billingData?.subscription?.billingPeriod || "monthly";
                    setAssignPlanId(currentPlanId || null);
                    setAssignPeriod(currentPeriod);
                    setAssignPaymentStatus("full");
                    setAssignAmountPaid(0);
                  }}
                >
                  Assign Plan
                </Button>
                {billingData?.subscription &&
                  !billingData.subscription.cancelAtPeriodEnd &&
                  ["active", "trialing", "paused"].includes(billingData.subscription.status) && (
                  <Popconfirm
                    title="Cancel this subscription?"
                    description="The subscription will be cancelled at the end of the current billing period."
                    onConfirm={handleCancelSubscription}
                    okText="Yes, Cancel"
                    okButtonProps={{ danger: true }}
                    cancelText="Keep"
                  >
                    <Button danger size="small" icon={<StopOutlined />} loading={cancelLoading}>
                      Cancel Subscription
                    </Button>
                  </Popconfirm>
                )}
                {billingData?.subscription?.cancelAtPeriodEnd && (
                  <Tag color="orange" style={{ margin: 0 }}>Cancels at period end</Tag>
                )}
              </Space>
            </div>
            <Descriptions size="small" column={2} bordered>
              {/* Trial rows — always shown if trial data exists */}
              {billingData?.user?.trialStartedAt && (
                <Descriptions.Item label="Trial Started" span={1}>
                  {dayjs(billingData.user.trialStartedAt).format("MMM D, YYYY")}
                </Descriptions.Item>
              )}
              {billingData?.user?.trialEndsAt && (
                <Descriptions.Item label="Trial Ends" span={1}>
                  {dayjs(billingData.user.trialEndsAt).format("MMM D, YYYY")}
                  {billingData.user.planStatus === "trial" && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      {Math.max(0, Math.ceil((new Date(billingData.user.trialEndsAt) - new Date()) / 86400000))} days left
                    </Tag>
                  )}
                </Descriptions.Item>
              )}

              {/* Subscription rows — shown if subscription exists */}
              {billingData?.subscription && (
                <>
                  <Descriptions.Item label="Plan">{billingData.subscription.planId?.name || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Billing Period" style={{ textTransform: "capitalize" }}>
                    {billingData.subscription.billingPeriod || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period Start">
                    {billingData.subscription.currentPeriodStart ? dayjs(billingData.subscription.currentPeriodStart).format("MMM D, YYYY") : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period End">
                    {billingData.subscription.currentPeriodEnd ? dayjs(billingData.subscription.currentPeriodEnd).format("MMM D, YYYY") : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Agent Seats">
                    {billingData.subscription.agentCount || 1} seats
                    {billingData.subscription.planId?.agentPrice > 0 && (
                      <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                        (${billingData.subscription.planId.agentPrice}/extra/mo)
                      </span>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    {billingData.subscription.cancelAtPeriodEnd
                      ? <Tag color="orange">Cancels at period end</Tag>
                      : <Tag color={statusColor[billingData.subscription.status] || "default"}>{billingData.subscription.status?.toUpperCase()}</Tag>
                    }
                  </Descriptions.Item>
                </>
              )}

              {/* No subscription yet */}
              {!billingData?.subscription && (
                <Descriptions.Item label="Subscription" span={2}>
                  <Tag color="default">No active subscription</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          {/* Invoice table */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h6 style={{ fontWeight: 600, margin: 0 }}>Payment History</h6>
            <Input.Search
              placeholder="Search invoice #, plan, status..."
              allowClear
              style={{ width: 280 }}
              value={invoiceSearch}
              onChange={(e) => setInvoiceSearch(e.target.value)}
            />
          </div>
          <Table
            columns={invoiceColumns}
            dataSource={(billingData?.invoices || []).filter((inv) => {
              if (!invoiceSearch) return true;
              const q = invoiceSearch.toLowerCase();
              return (
                (inv.invoiceNumber || "").toLowerCase().includes(q) ||
                (inv.planId?.name || "").toLowerCase().includes(q) ||
                (inv.status || "").toLowerCase().includes(q)
              );
            })}
            rowKey="_id"
            loading={billingLoading}
            pagination={{
              total: billingData?.totalInvoices || 0,
              pageSize: 10,
              current: billingPage,
              onChange: (p) => setBillingPage(p),
              showTotal: (t) => `${t} invoices`,
            }}
            locale={{ emptyText: <div style={{ padding: 32, color: "#9ca3af" }}><CreditCardOutlined style={{ fontSize: 32, marginBottom: 8, display: "block" }} />No payment history</div> }}
            bordered
          />
        </div>
      ),
    },
  ];

  if (!companyDetails) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center">
            <p>No company details available</p>
            <Button onClick={() => navigate(all_routes.adminCompaniesList)}>
              Back to Companies
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Contact Head - Similar to customer details */}
            <div className="contact-head">
              <div className="row align-items-center">
                <div className="col-sm-6">
                  <ul className="contact-breadcrumb">
                    <li>
                      <Link to={all_routes.adminCompaniesList}>
                        <i className="ti ti-arrow-narrow-left" />
                        Companies
                      </Link>
                    </li>
                    <li>
                      {companyDetails.firstname
                        ? `${companyDetails.firstname} ${companyDetails.lastname}`
                        : "N/A"}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Company Info Card - Similar to customer card */}
            <div className="card">
              <div className="card-body pb-2">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <div className="d-flex align-items-start  mb-2">
                    <span className="avatar avatar-xl bg-primary flex-shrink-0 me-3">
                      <span className="avatar-title text-white">
                        {companyDetails.firstname
                          ? companyDetails.firstname[0]
                          : "N"}
                        {companyDetails.lastname
                          ? companyDetails.lastname[0]
                          : "A"}
                      </span>
                    </span>
                    <div>
                      <h5 className="mb-0 mt-1">
                        {companyDetails.firstname
                          ? `${companyDetails.firstname} ${companyDetails.lastname}`
                          : "N/A"}
                      </h5>
                      <div className="d-flex mt-2">
                        <span className="me-1 text-dark">
                          <MailOutlined style={{ marginRight: 4 }} />
                          {companyDetails.email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="contacts-action align-items-center mb-2">
                    <div className="d-flex align-items-end gap-2">
                      <div className="d-flex flex-column align-items-center">
                        <small className="text-muted mb-1">Extension</small>
                        <Switch
                          checked={localExtensionStatus}
                          loading={toggleLoading}
                          onChange={handleToggleExtensionStatus}
                          checkedChildren={"Active"}
                          unCheckedChildren={"Inactive"}
                          title={
                            localExtensionStatus
                              ? "Deactivate extension"
                              : "Activate extension"
                          }
                        />
                      </div>
                      <div className="d-flex flex-column align-items-center">
                        <small className="text-muted mb-1">Account</small>
                        <Switch
                          checked={localAccountStatus === "active"}
                          loading={accountToggleLoading}
                          onChange={handleToggleAccountStatus}
                          checkedChildren={"Active"}
                          unCheckedChildren={"Suspended"}
                          title={
                            localAccountStatus === "active"
                              ? "Suspend account"
                              : "Activate account"
                          }
                        />
                      </div>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyLoginLink(companyDetails._id)}
                      >
                        Copy Login Link
                      </Button>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={handleEditCompany}
                      >
                        Edit Company
                      </Button>
                    </div>
                  </div>
                </div>

                {/* ── Credit Balance chip ── */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 16px", background: "#f9fafb",
                  borderTop: "1px solid #f0f0f0",
                }}>
                  <DollarOutlined style={{ color: "#6b7280", fontSize: 14 }} />
                  <span style={{ fontSize: 13, color: "#6b7280" }}>Credit Balance</span>
                  <span style={{
                    fontWeight: 800, fontSize: 16,
                    color: (liveBalance ?? companyDetails?.creditBalance ?? 0) < 5 ? "#ef4444" : "#10b981",
                  }}>
                    ${Number(liveBalance ?? companyDetails?.creditBalance ?? 0).toFixed(2)}
                  </span>
                  <Button
                    size="small"
                    icon={<DollarOutlined />}
                    onClick={openBalanceModal}
                    style={{ marginLeft: 4 }}
                  >
                    Adjust
                  </Button>
                </div>
              </div>
            </div>

            {/* Company Details Card */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Extension Number
                      </label>
                      <div className="fw-semibold">
                        <Tag color="blue">
                          {companyDetails?.PBXDetails?.PBX_EXTENSION_NUMBER || "N/A"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Telephone Number
                      </label>
                      <div className="fw-semibold">
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        {companyDetails?.PBXDetails?.PBX_TELEPHONE|| "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">Created At</label>
                      <div className="fw-semibold">
                        {new Date(
                          companyDetails.createdAt
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Extension Status
                      </label>
                      <div className="fw-semibold">
                        <Tag
                          color={
                            companyDetails.extensionStatus ? "green" : "red"
                          }
                        >
                          {companyDetails.extensionStatus
                            ? "Active"
                            : "Inactive"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">Account Status</label>
                      <div className="fw-semibold">
                        <Tag
                          color={
                            companyDetails.accountStatus === "active"
                              ? "green"
                              : companyDetails.accountStatus === "suspended"
                              ? "orange"
                              : "red"
                          }
                        >
                          {companyDetails.accountStatus === "active"
                            ? "Active"
                            : companyDetails.accountStatus === "suspended"
                            ? "Suspended"
                            : "Deactivated"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="text-muted small">
                        Phone number(s)
                      </label>
                      <div className="fw-semibold">
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        {companyDetails?.phonenumbers?.[0]
                          ? `${
                              companyDetails?.phonenumbers[0].countryCode || ""
                            } ${
                              companyDetails?.phonenumbers[0].number || ""
                            }`.trim()
                          : "No phone numbers"}
                      </div>
                    </div>
                  </div>
                  {/* Auto-analyze AI toggle hidden
                  <div className="col-12 mt-3">
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                      <div>
                        <div className="fw-semibold">Auto-analyze calls with AI</div>
                        <div className="text-muted small">
                          Automatically transcribe and score every call after it ends using Gemini AI
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {savingTranscribe && <span className="spinner-border spinner-border-sm" />}
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="autoTranscribeSwitch"
                            checked={autoTranscribeEnabled}
                            onChange={(e) => handleTranscribeToggle(e.target.checked)}
                            disabled={savingTranscribe}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  */}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="card">
              <div className="card-body">
                <Tabs defaultActiveKey="agents" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mark as Paid Modal */}
      <Modal
        title={<Space><DollarOutlined style={{ color: "#059669" }} /> Mark Invoice as Paid</Space>}
        open={markPaidModal}
        onCancel={() => { setMarkPaidModal(false); setMarkPaidInvoice(null); setMarkPaidAmount(0); }}
        onOk={handleMarkPaid}
        confirmLoading={markPaidLoading}
        okText="Mark as Paid"
        okButtonProps={{ style: { background: "#059669" } }}
        width={400}
      >
        {markPaidInvoice && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8 }}>
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "#6b7280" }}>Invoice</span>
                <span style={{ fontWeight: 600 }}>{markPaidInvoice.invoiceNumber || markPaidInvoice.stripeInvoiceId || "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "#6b7280" }}>Total</span>
                <span style={{ fontWeight: 600 }}>${((markPaidInvoice.amount || 0) / 100).toFixed(2)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#6b7280" }}>Already paid</span>
                <span style={{ fontWeight: 600, color: "#059669" }}>${((markPaidInvoice.amountPaid || 0) / 100).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Amount Being Paid Now ($)</label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                max={(markPaidInvoice.amount || 0) / 100}
                precision={2}
                prefix="$"
                value={markPaidAmount}
                onChange={(v) => setMarkPaidAmount(v || 0)}
              />
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                Set to full pending amount or enter a partial payment.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Plan Modal */}
      <Modal
        title={<Space><UserAddOutlined style={{ color: "#6366f1" }} /> Assign Plan to {companyDetails?.firstname} {companyDetails?.lastname}</Space>}
        open={assignModal}
        onCancel={() => setAssignModal(false)}
        onOk={handleAssignPlan}
        confirmLoading={assignLoading}
        okText="Assign Plan"
        okButtonProps={{ style: { background: "#6366f1" }, disabled: !assignPlanId }}
        width={480}
      >
        {(() => {
          const selectedPlan = (allPlans || []).find((p) => p._id === assignPlanId);
          const _mult = { monthly: 1, quarterly: 3, semiannual: 6, yearly: 12 }[assignPeriod] || 1;
          const _raw = selectedPlan?.pricing?.[assignPeriod]?.price || 0;
          const _disc = selectedPlan?.pricing?.[assignPeriod]?.discountPercent || 0;
          const planPrice = _raw * _mult * (1 - _disc / 100);
          const pendingAmount = assignPaymentStatus === "full"
            ? 0
            : assignPaymentStatus === "pending"
              ? planPrice
              : Math.max(0, planPrice - (assignAmountPaid || 0));

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
              {/* Plan selector */}
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Select Plan</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Choose a plan"
                  value={assignPlanId}
                  onChange={(val) => {
                    setAssignPlanId(val);
                    const plan = (allPlans || []).find((p) => p._id === val);
                    const periods = ["monthly", "quarterly", "semiannual", "yearly"];
                    const activePeriod = periods.find((p) => plan?.pricing?.[p]?.price > 0);
                    if (activePeriod) setAssignPeriod(activePeriod);
                  }}
                >
                  {(allPlans || []).filter((p) => !p.isEnterprise && p.isActive).map((p) => (
                    <Select.Option key={p._id} value={p._id}>{p.name}</Select.Option>
                  ))}
                </Select>
              </div>

              {/* Billing period */}
              {assignPlanId && (
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Billing Period</label>
                  <Select style={{ width: "100%" }} value={assignPeriod} onChange={setAssignPeriod}>
                    {["monthly", "quarterly", "semiannual", "yearly"].filter(
                      (p) => selectedPlan?.pricing?.[p]?.price > 0
                    ).map((p) => {
                      const m = { monthly: 1, quarterly: 3, semiannual: 6, yearly: 12 }[p] || 1;
                      const r = selectedPlan?.pricing?.[p]?.price || 0;
                      const d = selectedPlan?.pricing?.[p]?.discountPercent || 0;
                      const effective = (r * m * (1 - d / 100)).toFixed(2);
                      return (
                        <Select.Option key={p} value={p}>
                          {p.charAt(0).toUpperCase() + p.slice(1)} — ${effective}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
              )}

              {/* Payment tracking */}
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Payment Status</label>
                <Select style={{ width: "100%" }} value={assignPaymentStatus} onChange={(v) => { setAssignPaymentStatus(v); setAssignAmountPaid(0); }}>
                  <Select.Option value="full">Fully Paid</Select.Option>
                  <Select.Option value="partial">Partially Paid</Select.Option>
                  <Select.Option value="pending">Pending (Not Paid)</Select.Option>
                </Select>
              </div>

              {assignPaymentStatus === "partial" && (
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>Amount Paid ($)</label>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={planPrice}
                    precision={2}
                    prefix="$"
                    value={assignAmountPaid}
                    onChange={(v) => setAssignAmountPaid(v || 0)}
                  />
                </div>
              )}

              {/* Credits — auto-applied when there's a pending amount */}
              {assignPlanId && pendingAmount > 0 && (companyDetails?.creditBalance || 0) > 0 && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 600, color: "#15803d" }}>Available Credits: ${(companyDetails.creditBalance || 0).toFixed(2)}</span>
                    <span style={{ color: "#166534", fontSize: 12 }}>
                      Auto-applying ${Math.min(companyDetails.creditBalance, pendingAmount).toFixed(2)} toward pending balance
                    </span>
                  </div>
                </div>
              )}

              {/* Payment summary */}
              {assignPlanId && (
                <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ color: "#6b7280" }}>Plan price</span>
                    <span style={{ fontWeight: 600 }}>${planPrice.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ color: "#6b7280" }}>Amount paid</span>
                    <span style={{ fontWeight: 600, color: "#059669" }}>
                      ${(() => {
                        const basePaid = assignPaymentStatus === "full" ? planPrice : assignPaymentStatus === "pending" ? 0 : assignAmountPaid;
                        const creditApplied = pendingAmount > 0 ? Math.min(companyDetails?.creditBalance || 0, pendingAmount) : 0;
                        return (basePaid + creditApplied).toFixed(2);
                      })()}
                    </span>
                  </div>
                  {pendingAmount > 0 && (companyDetails?.creditBalance || 0) > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: "#15803d" }}>Credits applied</span>
                      <span style={{ fontWeight: 600, color: "#15803d" }}>-${Math.min(companyDetails?.creditBalance || 0, pendingAmount).toFixed(2)}</span>
                    </div>
                  )}
                  {pendingAmount > 0 && (companyDetails?.creditBalance || 0) < pendingAmount && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Remaining pending</span>
                      <span style={{ fontWeight: 600, color: "#f59e0b" }}>
                        ${Math.max(0, pendingAmount - Math.min(companyDetails?.creditBalance || 0, pendingAmount)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#92400e" }}>
                This will immediately assign the selected plan to this company, overriding any existing subscription. No Stripe charge is created.
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Edit Company Offcanvas */}
      {editCompanyVisible && (
        <EditCompanyOffcanvas
          visible={editCompanyVisible}
          onClose={handleCompanyOffcanvasClose}
          company={companyDetails}
        />
      )}

      {/* Edit Agent Offcanvas */}
      {editAgentVisible && selectedAgent && (
        <EditAgentOffcanvas
          visible={editAgentVisible}
          onClose={handleAgentOffcanvasClose}
          agent={selectedAgent}
        />
      )}

      {/* Reset Agent Password Modal */}
      <Modal
        title={<span><LockOutlined style={{ marginRight: 8, color: "#f59e0b" }} />Reset Password{resetPwdTarget ? ` — ${resetPwdTarget.email}` : ""}</span>}
        open={resetPwdModal}
        onCancel={() => setResetPwdModal(false)}
        onOk={handleAgentResetPassword}
        okText="Reset"
        confirmLoading={resetPwdLoading}
        width={400}
      >
        <Form form={resetPwdForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: "Password is required" },
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  if (value.length < 8) return Promise.reject(new Error("At least 8 characters required"));
                  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(value))
                    return Promise.reject(new Error("Must include uppercase, lowercase, number and special character (@$!%*?&#)"));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm the password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Agent Email / Mobile Modal */}
      <Modal
        title={<span><MailOutlined style={{ marginRight: 8, color: "#3b82f6" }} />Change Email / Mobile{contactTarget ? ` — ${contactTarget.email}` : ""}</span>}
        open={contactModal}
        onCancel={() => setContactModal(false)}
        onOk={handleAgentUpdateContact}
        okText="Update"
        confirmLoading={contactLoading}
        width={420}
      >
        <Form form={contactForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Valid email required" }]}>
            <Input />
          </Form.Item>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="countryCode" label="Country Code" style={{ width: 110 }}>
              <Input placeholder="+1" />
            </Form.Item>
            <Form.Item name="mobile" label="Mobile (optional)" style={{ flex: 1 }}>
              <Input placeholder="5551234567" />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Create Agent Modal */}
      <Modal
        title={<span><UserAddOutlined style={{ marginRight: 8, color: "#6366f1" }} />Create Agent</span>}
        open={createAgentModal}
        onCancel={() => { setCreateAgentModal(false); createAgentForm.resetFields(); }}
        onOk={handleCreateAgent}
        okText="Create"
        confirmLoading={createAgentLoading}
        width={480}
        destroyOnClose
      >
        <Form form={createAgentForm} layout="vertical" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="firstname" label="First Name" style={{ flex: 1 }} rules={[{ required: true, message: "Required" }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastname" label="Last Name" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Valid email required" }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Password is required" },
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  if (value.length < 8) return Promise.reject(new Error("At least 8 characters required"));
                  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(value))
                    return Promise.reject(new Error("Must include uppercase, lowercase, number and special character (@$!%*?&#)"));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="countryCode" label="Country Code" style={{ width: 110 }}>
              <Input placeholder="+1" />
            </Form.Item>
            <Form.Item name="mobile" label="Mobile (optional)" style={{ flex: 1 }}>
              <Input placeholder="5551234567" />
            </Form.Item>
          </div>
          <Form.Item name="emailVerified" valuePropName="checked">
            <Checkbox>Mark email as verified</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.emailVerified !== cur.emailVerified || prev.firstname !== cur.firstname}>
            {({ getFieldValue }) => {
              const emailOk = !!getFieldValue("emailVerified");
              const hasName = !!(getFieldValue("firstname") || "").trim();
              return emailOk && hasName ? (
                <Form.Item name="mobileVerified" valuePropName="checked">
                  <Checkbox>Mark mobile as verified</Checkbox>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Adjust Balance Modal ── */}
      <Modal
        title={
          <span>
            <DollarOutlined style={{ color: "#10b981", marginRight: 8 }} />
            Adjust Balance — {companyDetails?.userInfo?.companyName ||
              `${companyDetails?.firstname || ""} ${companyDetails?.lastname || ""}`.trim()}
          </span>
        }
        open={balanceModal}
        onCancel={() => setBalanceModal(false)}
        onOk={handleSaveBalance}
        okText="Save"
        confirmLoading={balanceSaving}
        width={400}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
          padding: "10px 14px", marginBottom: 16, fontSize: 13,
        }}>
          <span style={{ color: "#6b7280" }}>Current balance</span>
          <span style={{
            fontWeight: 700, fontSize: 16,
            color: (liveBalance ?? companyDetails?.creditBalance ?? 0) < 5 ? "#ef4444" : "#10b981",
          }}>
            ${Number(liveBalance ?? companyDetails?.creditBalance ?? 0).toFixed(2)}
          </span>
        </div>
        <Form form={balanceForm} layout="vertical">
          <Form.Item name="mode" label="Operation" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="set">Set to exact amount</Select.Option>
              <Select.Option value="add">Add to balance (top-up)</Select.Option>
              <Select.Option value="subtract">Subtract from balance (deduct)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount ($)"
            rules={[
              { required: true, message: "Enter an amount" },
              { validator: (_, v) => Number(v) >= 0 ? Promise.resolve() : Promise.reject("Amount must be ≥ 0") },
            ]}
          >
            <InputNumber min={0} precision={2} step={1} prefix="$" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input placeholder="e.g. Manual top-up by admin" maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default CompanyDetails;
